
import {distinctUntilChanged, debounce, startWith} from 'rxjs/operators';
import { Input, Component, OnInit, ElementRef, ViewChild, EventEmitter, Output, AfterViewInit } from '@angular/core';
import { UserService, UserWithEmplId, SortedSet, IdAndAlias, LocalUser } from '../../domain';
import { FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { partition, timer } from 'rxjs';
import { ConsoleService } from '../console.service';
import { MatAutocomplete } from '@angular/material/autocomplete';

@Component({
	selector: 'user-picker',
	templateUrl: './user-picker.component.html',
	styleUrls: ['./user-picker.component.scss'],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: UserPickerComponent,
			multi: true
		}
	]
})
export class UserPickerComponent implements OnInit, AfterViewInit {
ngAfterViewInit(): void {
		this.console.log('autoComplete: ', this.autoComplete);
	}
	@Output()
	public readonly newNonAnrCollaboratorRequest: EventEmitter<string> = new EventEmitter();
	@Input() controlClass: string = '';
	@Input() enableNonANRCollaborator: boolean = false;
	@Input() selectSingle: boolean = false;
	@Input() filterIds: number[] = [];
	@Input() updateUserTableFromDirectory: boolean = true;
	@Input() showUserIds = false;
	@Input() showDeletedUsers = false
	@Input() searchLocalOnly = false
	users = [];
	searchControl = new FormControl();
	@ViewChild('auto') autoComplete: MatAutocomplete;
	selectedUsers: SortedSet<UserWithEmplId> = new SortedSet<UserWithEmplId>();
	lastTerm: string = '';
	@ViewChild('input') input: ElementRef;
	formLabelClass = "col-4";
	formControlClass = "col-8";
	userIdAliasMap: IdAndAlias[] = [];
	private allLocalUsers: LocalUser[] = null;

	newNonAnrCollaborator(){
		this.newNonAnrCollaboratorRequest.emit(this.searchControl.value);
		this.autoComplete.showPanel = false;
	}

	removeUser(user: UserWithEmplId): void {
		if(this.selectedUsers.remove(user)){
			this._onChange(this.selectedUsers);
			this._refreshAvailableUsers();
		}
	}

	displayWith(user){
		return user.firstName + ' ' + user.lastName;
	}

	canAddUser(): boolean {
		return !this.selectSingle 
			|| this.selectedUsers.count == 0;
	}

	ngOnInit() { }

		constructor(
		private console: ConsoleService,
		private readonly svc: UserService
	){
	
		let [selections, termChanges] = 
			partition(
				this.searchControl
					.valueChanges
					.pipe(startWith(null)),
				o => o instanceof UserWithEmplId);

		selections
			.subscribe(o => {
				let user = <UserWithEmplId>o;
				if(this.canAddUser()){
					if(this.selectedUsers.add(user)){
						this._onChange(this.selectedUsers);
						if (this.updateUserTableFromDirectory)
							this.svc.importFromDirectoryIfNecessary(user.id);
					}
				}
				this.input.nativeElement.value = '';
				this.searchControl.setValue('');
				this._refreshAvailableUsers();
				this.input.nativeElement.blur();
			})

		// Longer debounce when the term looks like an employee ID (digits only) —
		// typing a 9-digit number shouldn't fire a search after each keystroke.
		termChanges.pipe(
			debounce((term: string) => {
				const isNumeric = typeof term === 'string' && /^\d+$/.test(term.trim());
				return timer(isNumeric ? 600 : 200);
			}),
			distinctUntilChanged(),)
			.subscribe(term => {
				this.lastTerm = term;
				if (this.canAddUser()) {
					this.console.log('refreshing available users');
					this._refreshAvailableUsers();
				} else {
					this.users = [];
				}
			});

		this.svc.getUserIdAliasMap()
			.then(data => {
				this.userIdAliasMap = data;
				this.console.log('User ID aliases loaded:', this.userIdAliasMap);
			});
	}

	refreshing = false;
	private _refreshAvailableUsers(): void {
		if (this.searchLocalOnly) {
			this._refreshLocalUsers();
		} else {
			this._refreshDirectoryUsers();
		}
	}

	private _refreshLocalUsers(): void {
		const doFilter = () => {
			const rawTerm = this.lastTerm || '';
			const term = rawTerm.toLowerCase();
			if (!term.trim()) {
				this.users = [];
				return;
			}
			// A word is "committed" (exact match required) if the user typed a
			// delimiter (space/comma) after it. Only the final word — if there's
			// no trailing delimiter — stays as a prefix match.
			// e.g. "chris"   → [{chris, prefix}]
			//      "chris "  → [{chris, exact}]
			//      "chris d" → [{chris, exact}, {d, prefix}]
			const trailingDelim = /[\s,]$/.test(rawTerm);
			const rawWords = term.split(/[\s,]+/).filter(w => w.length > 0);
			const words = rawWords.map((w, i) => ({
				value: w,
				exact: trailingDelim || i < rawWords.length - 1
			}));
			const matches = (haystack: string, w: { value: string, exact: boolean }) =>
				w.exact ? haystack === w.value : haystack.startsWith(w.value);
			this.users = this.allLocalUsers
				.filter(u => {
					const first = u.firstName.toLowerCase();
					const last = u.lastName.toLowerCase();
					const email = (u.email || '').toLowerCase();
					const emplId = (u.employeeId || '').toLowerCase();
					return words.every(w =>
						matches(first, w) || matches(last, w) || matches(email, w) || matches(emplId, w)
					);
				})
				// 'a' = Active DirUserStatus; template uses status === 'd' to gray out deleted
			// entries. Local users are only in this list if they're active, so 'a' is safe.
			.map(u => new UserWithEmplId(u.id, u.firstName, u.lastName, u.employeeId, 'a'))
				.filter(user => !this.selectedUsers.contains(user) && !this.filterIds.includes(user.id));
		};

		if (this.allLocalUsers) {
			doFilter();
		} else {
			this.refreshing = true;
			this.svc.getAllLocal().then(users => {
				this.allLocalUsers = users;
				this.refreshing = false;
				doFilter();
			});
		}
	}

	private _refreshDirectoryUsers(): void {
		this.refreshing = true;
		this.svc.search(this.lastTerm, 10, this.showDeletedUsers)
			.then(users => {

				users.forEach(user => {
					// Find the alias object that matches the user's id
					const alias = this.userIdAliasMap.find(alias => alias.userId === user.id);

					// If an alias is found, update the user's id to the userIdAlias
					if (alias) {
						user.id = alias.userIdAlias;
					}
				});

				this.refreshing = false;
				this.users = users.filter(user =>
					!this.selectedUsers.contains(user) && !this.filterIds.includes(user.id));
			});
	}
	
	//
	//ControlValueAccessor implementation:
	private _onChange: any = () => {};
	private _onTouch:  any = () => {};
	writeValue(val: SortedSet<UserWithEmplId>): void {
		//console.log('writeValue:', val);
		this.selectedUsers = val;
		//this._onChange(val);
	}
	registerOnTouched(fn): void { this._onTouch  = fn; }
	registerOnChange(fn) : void { this._onChange = fn; }
}
